import { IconNames } from "@blueprintjs/icons";
import {
    ScenarioMetadata,
    ScenarioOrigin,
    ScenarioStatus
} from "in_queue/types/scenarioType";

export enum ScenarioSelectionMode {
    Compare = "compare",
    Rerun = "rerun",
    Archive = "archive"
}

export interface ScenarioSelectionRules {
    isRowSelectable: (scenario: ScenarioMetadata) => boolean;
    selectionLimit: number | undefined;
    disabledReason: (scenario: ScenarioMetadata) => string;
}

// Shown when a user tries to select a non-successful scenario for
// comparison (draft / running / failed). Only RunSucceeded scenarios
// have results to compare.
export const COMPARE_DISABLED_REASON =
    "Only successfully run scenarios can be compared";

export const ARCHIVE_DISABLED_REASON = "Base scenarios can not be archived";

export const ALREADY_ARCHIVED_REASON = "This scenario is already archived";

export const RERUN_ARCHIVED_REASON =
    "Archived scenarios can not be duplicated and re-run";

export const BASE_RERUN_DISABLED_REASON =
    "Only your team's custom scenarios can be duplicated and re-run";

export const RERUN_RUNNING_REASON =
    "Scenarios that are still running can not be duplicated and re-run";

const IN_FLIGHT_STATUSES: ReadonlyArray<ScenarioStatus> = [
    ScenarioStatus.RunRequested,
    ScenarioStatus.RunSubmitted,
    ScenarioStatus.Running
];

export const scenarioSelectionRules = (
    mode: ScenarioSelectionMode
): ScenarioSelectionRules => {
    switch (mode) {
        case ScenarioSelectionMode.Compare:
            return {
                isRowSelectable: (scenario) =>
                    scenario.status === ScenarioStatus.RunSucceeded,
                selectionLimit: 2,
                disabledReason: () => COMPARE_DISABLED_REASON
            };
        case ScenarioSelectionMode.Rerun:
            return {
                isRowSelectable: (scenario) =>
                    scenario.origin === ScenarioOrigin.Custom &&
                    !scenario.archived &&
                    !IN_FLIGHT_STATUSES.includes(scenario.status),
                selectionLimit: undefined,
                disabledReason: (scenario) => {
                    if (scenario.origin !== ScenarioOrigin.Custom) {
                        return BASE_RERUN_DISABLED_REASON;
                    }
                    return scenario.archived
                        ? RERUN_ARCHIVED_REASON
                        : RERUN_RUNNING_REASON;
                }
            };
        case ScenarioSelectionMode.Archive:
            return {
                isRowSelectable: (scenario) =>
                    (scenario.type === "CUSTOM" ||
                        scenario.origin === ScenarioOrigin.NiraPublished) &&
                    !scenario.archived,
                selectionLimit: undefined,
                disabledReason: (scenario) =>
                    scenario.archived
                        ? ALREADY_ARCHIVED_REASON
                        : ARCHIVE_DISABLED_REASON
            };
    }
};

export const RERUN_VERB = "Duplicate & re-run";
export const RERUN_ICON = IconNames.REFRESH;
